import {useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent} from 'react';
import {useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';
import {AlertCircle, ChevronLeft, Delete, Grid3x3, Loader2, MicOff, MoveDiagonal2, Phone, Search, UserPlus, Users, Video, WifiOff, X} from 'lucide-react';
import type {RootState} from '../../../store/store';
import {setPhoneNumber} from '../../../store/features/calls/dialerSlice';
import {selectUserId} from '../../../store/features/Auth/authSlice';
import {useSearchContactSuggestionsQuery, type ContactIdentity} from '../../../store/features/Contacts/contactsService';
import {useFetchUsersQuery, type UserElement} from '../../../store/features/Users/userService';
import {useFetchMsisdnsListQuery} from '../../../store/features/MsisdnSettings/msisdnSettingsService';
import type {Msisdn} from '../../../store/features/Msisdns/msisdnService';
import {useDTMF} from '../../../components/Dialer/hooks/DTMFToneHook';
import {getFormatedPhoneNumber} from '../../../utilis/msisdnUtilis';
import {CallAvatar} from '../components/CallAvatar';
import {useCallDock} from '../components/callDock';
import {KEYPAD_KEYS} from '../components/keypadKeys';
import {selectCallSurfaceState, setDialerRefusal, type DialerNotice} from '../store/callSurfaceSlice';
import {LinePicker} from './LinePicker';
import {formatDialable, looksLikeNumber, regionOfLine, toDialableNumber} from './dialerNumbers';
import {useDialerActions} from './useDialerActions';
import {DIALER_MIN_WIDTH, dialerWidth} from './dialerWidth';
import {useRtcFeatures} from '../../groupCalls/hooks/useGroupCall';
import {startTeamGroupCall} from '../team/startTeamCall';
import {dialerMessages as m} from './dialerMessages';
import {refusalMatches} from './dialerRefusal';
import {RefusalRow} from './RefusalRow';

const MIN_SEARCH = 2;
const MAX_TEAM = 6;
const MAX_CONTACTS = 5;

type DialerOption =
    | {readonly kind: 'number'; readonly id: string; readonly e164: string}
    | {readonly kind: 'team'; readonly id: string; readonly user: UserElement}
    | {readonly kind: 'contact'; readonly id: string; readonly contact: ContactIdentity};

const fullName = (first?: string, last?: string): string => [first, last].filter(Boolean).join(' ');

const optionName = (option: DialerOption): string =>
    option.kind === 'number'
        ? formatDialable(option.e164)
        : option.kind === 'team'
            ? fullName(option.user.firstName, option.user.lastName)
            : fullName(option.contact.firstName, option.contact.lastName) || getFormatedPhoneNumber(option.contact.phoneNumber, false);

type Notice = DialerNotice | 'offline' | 'mic' | 'no_line';

const NOTICE_TEXT = {
    in_call: m.alreadyInCall,
    sip_error: m.sipError,
    no_number: m.noLine,
    no_line: m.noLine,
    offline: m.offline,
    mic: m.micBlocked,
} as const;

const NoticeRow = ({notice}: {notice: Notice}) => {
    const intl = useIntl();
    const Icon = notice === 'offline' ? WifiOff : notice === 'mic' ? MicOff : AlertCircle;
    return (
        <div role="status" className="flex items-start gap-2 rounded-xl bg-[var(--phq-call-warning-tint)] px-3 py-2 text-[13px] font-medium text-[var(--phq-call-warning-ink)]">
            <Icon size={16} className="mt-0.5 shrink-0" aria-hidden="true"/>
            <span>{intl.formatMessage(NOTICE_TEXT[notice])}</span>
        </div>
    );
};

export type DialerMode = 'phone' | 'team';

const useDialerOptions = (text: string, line: Msisdn | undefined, mode: DialerMode): readonly DialerOption[] => {
    const userId = useSelector(selectUserId);
    const phrase = text.trim();
    const searching = phrase.length >= MIN_SEARCH && (mode === 'team' || !looksLikeNumber(phrase));
    const {data: contacts} = useSearchContactSuggestionsQuery({phrase}, {skip: !searching || mode !== 'phone'});
    const {data: users} = useFetchUsersQuery({}, {skip: !searching || mode !== 'team'});
    return useMemo(() => {
        const options: DialerOption[] = [];
        if (mode === 'team') {
            if (searching) {
                const needle = phrase.toLowerCase();
                (users ?? [])
                    .filter(user => String(user.userId ?? user.id) !== String(userId))
                    .filter(user => fullName(user.firstName, user.lastName).toLowerCase().includes(needle))
                    .slice(0, MAX_TEAM)
                    .forEach(user => options.push({kind: 'team', id: `team:${user.userId ?? user.id}`, user}));
            }
            return options;
        }
        const e164 = looksLikeNumber(phrase) ? toDialableNumber(phrase, regionOfLine(line?.msisdn)) : undefined;
        if (e164) {
            options.push({kind: 'number', id: `number:${e164}`, e164});
        }
        if (searching) {
            (contacts ?? [])
                .slice(0, MAX_CONTACTS)
                .forEach(entry => options.push({kind: 'contact', id: `contact:${entry.primary.contactId}`, contact: entry.primary}));
        }
        return options;
    }, [contacts, line?.msisdn, mode, phrase, searching, userId, users]);
};

const RESIZE_STEP = 16;

const ResizeGrip = () => {
    const intl = useIntl();
    const anchor = useCallDock();
    const drag = useRef<{readonly pointerId: number; readonly startX: number; readonly startWidth: number}>();
    // The grip sits on the free side, so dragging away from the docked edge widens the bubble.
    const direction = anchor.horizontal === 'right' ? -1 : 1;

    const onPointerDown = (event: PointerEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        drag.current = {pointerId: event.pointerId, startX: event.clientX, startWidth: dialerWidth.get()};
    };
    const onPointerMove = (event: PointerEvent<HTMLButtonElement>) => {
        const state = drag.current;
        if (state?.pointerId === event.pointerId) {
            dialerWidth.set(state.startWidth + (event.clientX - state.startX) * direction);
        }
    };
    const onPointerEnd = () => {
        drag.current = undefined;
    };
    const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            const step = event.key === 'ArrowLeft' ? -RESIZE_STEP : RESIZE_STEP;
            dialerWidth.set(dialerWidth.get() + step * direction);
        }
    };

    return (
        <button
            type="button"
            data-testid="dialer-resize"
            aria-label={intl.formatMessage(m.resize)}
            title={intl.formatMessage(m.resizeHint)}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerEnd}
            onPointerCancel={onPointerEnd}
            onDoubleClick={() => dialerWidth.set(DIALER_MIN_WIDTH)}
            onKeyDown={onKeyDown}
            className={`absolute top-0 z-[3] inline-flex h-6 w-6 touch-none items-center justify-center text-[var(--phq-call-ink-muted)] ${
                anchor.horizontal === 'right' ? 'left-0 cursor-nwse-resize rounded-[20px_0_8px_0]' : 'right-0 cursor-nesw-resize rounded-[0_20px_0_8px]'
            }`}
        >
            <MoveDiagonal2 size={12} className={anchor.horizontal === 'right' ? '' : '-scale-x-100'} aria-hidden="true"/>
        </button>
    );
};

const ModeTabs = ({mode, onChange}: {mode: DialerMode; onChange: (mode: DialerMode) => void}) => {
    const intl = useIntl();
    const tabs = [
        {value: 'phone', label: m.phoneMode, Icon: Phone},
        {value: 'team', label: m.teamMode, Icon: Users},
    ] as const;
    return (
        <div role="tablist" aria-label={intl.formatMessage(m.callType)} className="flex h-9 shrink-0 gap-0.5 rounded-[10px] bg-[var(--phq-call-surface-muted)] p-0.5">
            {tabs.map(({value, label, Icon}) => {
                const selected = mode === value;
                return (
                    <button
                        key={value}
                        type="button"
                        role="tab"
                        aria-selected={selected}
                        data-testid={`dialer-mode-${value}`}
                        onClick={() => onChange(value)}
                        className={`phq-call-press inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg text-[13px] font-semibold ${
                            selected ? 'bg-[var(--phq-call-brand)] text-white shadow-[0_1px_2px_rgba(0,0,0,.2)]' : 'text-[var(--phq-call-ink-secondary)]'
                        }`}
                    >
                        <Icon size={14} aria-hidden="true"/>
                        {intl.formatMessage(label)}
                    </button>
                );
            })}
        </div>
    );
};

interface DialerBubbleProps {
    readonly onClose: () => void;
}

export const DialerBubble = ({onClose}: DialerBubbleProps) => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const listId = useId();
    const input = useRef<HTMLInputElement>(null);
    const text = useSelector((state: RootState) => state.dialer.phoneNumber) ?? '';
    const line = useSelector((state: RootState) => state.dialer.selectedPhoneNumber) as Msisdn | undefined;
    const isOffline = useSelector((state: RootState) => state.dialer.isOffline);
    const micRequired = useSelector((state: RootState) => state.dialer.micRequired);
    const {dialerNotice, dialerRefusal} = useSelector(selectCallSurfaceState);
    const userId = useSelector(selectUserId);
    const {data: lines, isFetching: linesLoading} = useFetchMsisdnsListQuery({userId});
    const [mode, setMode] = useState<DialerMode>('phone');
    const [view, setView] = useState<'search' | 'keypad'>('search');
    const [active, setActive] = useState(0);
    const [invalid, setInvalid] = useState(false);
    const options = useDialerOptions(text, line, mode);
    const {callNumber, callTeamMember} = useDialerActions();
    const {playToneForDuration} = useDTMF({volume: 0.1});
    const {groupCalls, videoCalls} = useRtcFeatures();
    const [picked, setPicked] = useState<readonly UserElement[]>([]);
    const [startingGroup, setStartingGroup] = useState(false);
    const [groupFailed, setGroupFailed] = useState(false);
    const pickedIds = new Set(picked.map(user => String(user.userId ?? user.id)));

    const pick = (user: UserElement) => {
        if (!pickedIds.has(String(user.userId ?? user.id))) {
            setPicked(current => [...current, user]);
        }
        setText('');
        input.current?.focus();
    };

    const unpick = (user: UserElement) => setPicked(current => current.filter(other => (other.userId ?? other.id) !== (user.userId ?? user.id)));

    const callPicked = async () => {
        if (picked.length === 1) {
            callTeamMember(picked[0]);
            return;
        }
        setStartingGroup(true);
        setGroupFailed(false);
        const outcome = await startTeamGroupCall(picked.map(user => Number(user.userId ?? user.id)), 'audio');
        setStartingGroup(false);
        if (outcome === 'started') {
            setPicked([]);
            onClose();
        } else {
            setGroupFailed(outcome === 'failed');
        }
    };

    const phoneNotice: Notice | undefined = isOffline
        ? 'offline'
        : micRequired
            ? 'mic'
            : !linesLoading && (lines?.data?.length ?? 0) === 0
                ? 'no_line'
                : dialerNotice;
    // Team calls go over Matrix, so only being offline stops them.
    const notice: Notice | undefined = mode === 'team' ? (isOffline ? 'offline' : undefined) : phoneNotice;

    useEffect(() => {
        input.current?.focus();
    }, [view, mode]);

    const switchMode = (next: DialerMode) => {
        if (next === mode) {
            return;
        }
        setMode(next);
        setView('search');
        setText('');
    };

    useEffect(() => {
        setActive(0);
        setInvalid(false);
    }, [text]);

    const refusal = refusalMatches(dialerRefusal, text) ? dialerRefusal : undefined;
    useEffect(() => {
        if (dialerRefusal && !refusalMatches(dialerRefusal, text)) {
            dispatch(setDialerRefusal(undefined));
        }
    }, [dialerRefusal, dispatch, text]);

    const setText = (value: string) => dispatch(setPhoneNumber(value));

    const phoneBlocked = !!phoneNotice && phoneNotice !== 'in_call';
    const isBlocked = (option: DialerOption | undefined): boolean =>
        option?.kind === 'team' ? isOffline : phoneBlocked;

    const choose = (option: DialerOption | undefined, video = false) => {
        if (!option) {
            if (text.trim()) {
                setInvalid(true);
            }
            return;
        }
        if (isBlocked(option)) {
            return;
        }
        if (option.kind === 'team') {
            callTeamMember(option.user, {video});
        } else if (option.kind === 'contact') {
            callNumber(`+${String(option.contact.phoneNumber).replace(/^\+/, '')}`);
        } else {
            callNumber(option.e164);
        }
    };

    const dialTyped = () => {
        if (phoneBlocked) {
            return;
        }
        const e164 = toDialableNumber(text, regionOfLine(line?.msisdn));
        if (e164) {
            callNumber(e164);
        } else {
            setInvalid(true);
        }
    };

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            const step = event.key === 'ArrowDown' ? 1 : -1;
            setActive(current => (options.length ? (current + step + options.length) % options.length : 0));
        } else if (event.key === 'Enter') {
            event.preventDefault();
            if (view === 'keypad') {
                dialTyped();
            } else {
                choose(options[active]);
            }
        }
    };

    const highlighted = options[active];

    return (
        <div className="flex flex-col gap-3 p-4" data-testid="dialer" data-mode={mode}>
            <ResizeGrip/>
            <header className="-mx-4 -mt-4 mb-1 flex h-14 items-center justify-between border-b border-[var(--phq-call-line)] pl-4 pr-2">
                {view === 'keypad' ? (
                    <button
                        type="button"
                        aria-label={intl.formatMessage(m.backToSearch)}
                        onClick={() => setView('search')}
                        className="phq-call-press -ml-2 inline-flex h-9 items-center gap-1 rounded-[10px] px-2 text-base font-semibold hover:bg-[var(--phq-call-surface-muted)]"
                    >
                        <ChevronLeft size={18} aria-hidden="true"/>
                        {intl.formatMessage(m.title)}
                    </button>
                ) : (
                    <h2 className="text-base font-semibold">{intl.formatMessage(m.title)}</h2>
                )}
                <button
                    type="button"
                    aria-label={intl.formatMessage(m.close)}
                    onClick={onClose}
                    className="phq-call-press inline-flex h-9 w-9 items-center justify-center rounded-[10px] hover:bg-[var(--phq-call-surface-muted)]"
                >
                    <X size={18} aria-hidden="true"/>
                </button>
            </header>
            <ModeTabs mode={mode} onChange={switchMode}/>
            {mode === 'phone' && <LinePicker/>}
            {notice && <NoticeRow notice={notice}/>}
            {refusal && !notice && <RefusalRow refusal={refusal}/>}
            {view === 'search' ? (
                <>
                    {picked.length > 0 && (
                        <div className="flex flex-wrap gap-1.5" data-testid="dialer-picked">
                            {picked.map(user => {
                                const name = fullName(user.firstName, user.lastName);
                                return (
                                    <span key={String(user.userId ?? user.id)} className="inline-flex h-7 items-center gap-1 rounded-full bg-[var(--phq-call-brand-tint)] pl-2.5 pr-1 text-[13px] font-semibold text-[var(--phq-call-brand-ink)]">
                                        {name}
                                        <button
                                            type="button"
                                            aria-label={intl.formatMessage(m.removePerson, {name})}
                                            onClick={() => unpick(user)}
                                            className="inline-flex h-5 w-5 items-center justify-center rounded-full hover:bg-[var(--phq-call-surface)]"
                                        >
                                            <X size={12} aria-hidden="true"/>
                                        </button>
                                    </span>
                                );
                            })}
                        </div>
                    )}
                    <label className="flex h-11 items-center gap-2 rounded-[10px] bg-[var(--phq-call-surface-muted)] px-3 focus-within:shadow-[inset_0_0_0_2px_var(--phq-call-brand-focus)]">
                        <Search size={16} className="shrink-0 text-[var(--phq-call-ink-muted)]" aria-hidden="true"/>
                        <input
                            ref={input}
                            type="search"
                            name="dialer"
                            phq-id="dialpad-typedNumberInput"
                            role="combobox"
                            aria-expanded={options.length > 0}
                            aria-controls={listId}
                            aria-activedescendant={highlighted ? `${listId}-${highlighted.id}` : undefined}
                            aria-invalid={invalid || undefined}
                            aria-label={intl.formatMessage(mode === 'team' ? m.searchTeam : m.searchPlaceholder)}
                            placeholder={intl.formatMessage(mode === 'team' ? m.searchTeam : m.searchPlaceholder)}
                            value={text}
                            onChange={event => {
                                // Z52: a bare number typed in Team belongs to Phone.
                                if (mode === 'team' && looksLikeNumber(event.target.value)) {
                                    setMode('phone');
                                }
                                setText(event.target.value);
                            }}
                            onKeyDown={onKeyDown}
                            className="min-w-0 flex-1 bg-transparent text-base font-medium outline-none placeholder:text-[var(--phq-call-ink-muted)]"
                        />
                    </label>
                    {invalid && <p role="alert" className="-mt-1 text-[13px] font-medium text-[var(--phq-call-danger)]">{intl.formatMessage(m.invalidNumber)}</p>}
                    {options.length > 0 && (
                        <div className="flex flex-col gap-1">
                            <p className="px-2 pt-1 text-[13px] font-semibold text-[var(--phq-call-ink-muted)]">{intl.formatMessage(mode === 'team' ? m.team : m.contacts)}</p>
                            <div id={listId} role="listbox" aria-label={intl.formatMessage(mode === 'team' ? m.matchingTeam : m.matching)} className="flex max-h-60 flex-col overflow-y-auto">
                                {options.map((option, index) => {
                                    const selected = index === active;
                                    const name = optionName(option);
                                    const subline = option.kind === 'team'
                                        ? option.user.position ?? option.user.email
                                        : option.kind === 'contact'
                                            ? getFormatedPhoneNumber(option.contact.phoneNumber, false)
                                            : undefined;
                                    return (
                                        <div
                                            key={option.id}
                                            id={`${listId}-${option.id}`}
                                            role="option"
                                            aria-selected={selected}
                                            onMouseEnter={() => setActive(index)}
                                            onClick={() => choose(option)}
                                            className={`flex h-12 cursor-pointer items-center gap-2.5 rounded-[10px] px-2 ${selected ? 'bg-[var(--phq-call-brand-tint-subtle)]' : ''}`}
                                        >
                                            {option.kind === 'number' ? (
                                                <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-[var(--phq-call-surface-muted)]">
                                                    <Phone size={16} aria-hidden="true"/>
                                                </span>
                                            ) : (
                                                <CallAvatar identity={{name, userId: option.kind === 'team' ? String(option.user.userId ?? option.user.id) : undefined}} size={32}/>
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-sm font-semibold">{name}</p>
                                                {subline && <p className="truncate text-[13px] text-[var(--phq-call-ink-muted)]">{subline}</p>}
                                            </div>
                                            {selected && <span className="shrink-0 text-[13px] text-[var(--phq-call-ink-strong-muted)]">{intl.formatMessage(m.enter)}</span>}
                                            {groupCalls && option.kind === 'team' && !pickedIds.has(String(option.user.userId ?? option.user.id)) && (
                                                <button
                                                    type="button"
                                                    data-testid="dialer-add-person"
                                                    aria-label={intl.formatMessage(m.addToCall, {name})}
                                                    onClick={event => {
                                                        event.stopPropagation();
                                                        pick(option.user);
                                                    }}
                                                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] text-[var(--phq-call-ink-strong-muted)] hover:bg-[var(--phq-call-surface)]"
                                                >
                                                    <UserPlus size={16} aria-hidden="true"/>
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {options.length === 0 && text.trim().length >= MIN_SEARCH && (mode === 'team' || !looksLikeNumber(text)) && (
                        <p className="px-2 text-[13px] text-[var(--phq-call-ink-muted)]">{intl.formatMessage(mode === 'team' ? m.noTeamMatches : m.noMatches)}</p>
                    )}
                    {groupFailed && <p role="alert" className="text-[13px] font-medium text-[var(--phq-call-danger)]">{intl.formatMessage(m.groupCallFailed)}</p>}
                    {picked.length > 0 ? (
                        <div className="flex flex-col gap-1 pt-1">
                            {picked.length > 1 && <p className="px-1 text-[13px] text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.groupCallCount, {count: picked.length + 1})}</p>}
                            <button
                                type="button"
                                data-testid="dialer-call"
                                disabled={startingGroup || notice === 'offline'}
                                onClick={() => void callPicked()}
                                className="phq-call-press inline-flex h-10 items-center justify-center gap-2 rounded-[10px] bg-[var(--phq-call-brand)] px-3 text-sm font-semibold text-white disabled:bg-[var(--phq-call-brand-disabled)]"
                            >
                                {startingGroup ? <Loader2 size={16} className="animate-spin" aria-hidden="true"/> : <Phone size={16} aria-hidden="true"/>}
                                <span className="truncate">
                                    {picked.length === 1
                                        ? intl.formatMessage(m.callName, {name: fullName(picked[0].firstName, picked[0].lastName)})
                                        : intl.formatMessage(m.startGroupCall)}
                                </span>
                            </button>
                        </div>
                    ) : mode === 'team' ? (
                        <div className="flex gap-2 pt-1">
                            <button
                                type="button"
                                data-testid="dialer-call"
                                aria-label={highlighted ? intl.formatMessage(m.voiceCallName, {name: optionName(highlighted)}) : undefined}
                                disabled={!highlighted || isBlocked(highlighted)}
                                onClick={() => choose(highlighted)}
                                className={`phq-call-press inline-flex h-10 min-w-0 flex-1 items-center justify-center gap-2 rounded-[10px] px-3 text-sm font-semibold disabled:opacity-50 ${
                                    videoCalls ? 'bg-[var(--phq-call-surface-muted)]' : 'bg-[var(--phq-call-brand)] text-white'
                                }`}
                            >
                                <Phone size={16} aria-hidden="true"/>
                                {intl.formatMessage(m.call)}
                            </button>
                            {videoCalls && (
                                <button
                                    type="button"
                                    data-testid="dialer-video"
                                    aria-label={highlighted ? intl.formatMessage(m.videoCallName, {name: optionName(highlighted)}) : undefined}
                                    disabled={!highlighted || isBlocked(highlighted)}
                                    onClick={() => choose(highlighted, true)}
                                    className="phq-call-press inline-flex h-10 min-w-0 flex-1 items-center justify-center gap-2 rounded-[10px] bg-[var(--phq-call-brand)] px-3 text-sm font-semibold text-white disabled:bg-[var(--phq-call-brand-disabled)]"
                                >
                                    <Video size={16} aria-hidden="true"/>
                                    {intl.formatMessage(m.video)}
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="flex gap-2 pt-1">
                            <button
                                type="button"
                                aria-label={intl.formatMessage(m.keypad)}
                                onClick={() => setView('keypad')}
                                className="phq-call-press inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-[var(--phq-call-surface-muted)]"
                            >
                                <Grid3x3 size={16} aria-hidden="true"/>
                            </button>
                            <button
                                type="button"
                                data-testid="dialer-call"
                                aria-label={highlighted ? intl.formatMessage(m.callName, {name: optionName(highlighted)}) : undefined}
                                disabled={!highlighted || isBlocked(highlighted)}
                                onClick={() => choose(highlighted)}
                                className="phq-call-press inline-flex h-10 min-w-0 flex-1 items-center justify-center gap-2 rounded-[10px] bg-[var(--phq-call-brand)] px-3 text-sm font-semibold text-white disabled:bg-[var(--phq-call-brand-disabled)]"
                            >
                                <Phone size={16} aria-hidden="true"/>
                                {intl.formatMessage(m.call)}
                            </button>
                        </div>
                    )}
                </>
            ) : (
                <>
                    <div className="flex h-12 items-center gap-2">
                        <span className="w-9 shrink-0"/>
                        <input
                            ref={input}
                            name="dialer"
                            phq-id="dialpad-typedNumberInput"
                            inputMode="tel"
                            aria-label={intl.formatMessage(m.enterNumber)}
                            placeholder={intl.formatMessage(m.enterNumber)}
                            value={text}
                            onChange={event => setText(event.target.value)}
                            onKeyDown={onKeyDown}
                            className="min-w-0 flex-1 bg-transparent text-center text-2xl font-semibold leading-[1.2] tabular-nums outline-none placeholder:text-base placeholder:font-medium placeholder:text-[var(--phq-call-ink-muted)]"
                        />
                        <button
                            type="button"
                            aria-label={intl.formatMessage(m.deleteDigit)}
                            tabIndex={text ? 0 : -1}
                            onClick={() => setText(text.slice(0, -1))}
                            className={`phq-call-press inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] text-[var(--phq-call-ink-secondary)] hover:bg-[var(--phq-call-surface-muted)] ${text ? '' : 'invisible'}`}
                        >
                            <Delete size={20} aria-hidden="true"/>
                        </button>
                    </div>
                    {invalid && <p role="alert" className="-mt-2 text-center text-[13px] font-medium text-[var(--phq-call-danger)]">{intl.formatMessage(m.invalidNumber)}</p>}
                    <div className="grid grid-cols-3 gap-2">
                        {KEYPAD_KEYS.map(key => (
                            <button
                                key={key.digit}
                                type="button"
                                aria-label={key.digit}
                                onClick={() => {
                                    playToneForDuration(key.digit);
                                    setText(`${text}${key.digit}`);
                                }}
                                className="phq-call-press flex h-12 flex-col items-center justify-center rounded-[10px] bg-[var(--phq-call-surface-muted)]"
                            >
                                <span className="text-xl font-semibold leading-[1.1]">{key.digit}</span>
                                <span className="h-2.5 text-[11px] font-semibold leading-[10px] tracking-[.08em] text-[var(--phq-call-ink-strong-muted)]">{key.letters}</span>
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        data-testid="dialer-call"
                        disabled={!text.trim() || phoneBlocked}
                        onClick={dialTyped}
                        className="phq-call-press inline-flex h-11 items-center justify-center gap-2 rounded-[10px] bg-[var(--phq-call-brand)] text-sm font-semibold text-white disabled:bg-[var(--phq-call-brand-disabled)]"
                    >
                        <Phone size={16} aria-hidden="true"/>
                        {intl.formatMessage(m.call)}
                    </button>
                </>
            )}
        </div>
    );
};
